import { type WorkItem } from '@/features/jobs/bookkeeping/BookkeepingProgressionInfo'
import { isNil } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'

/**
 * A work item's completed count in the cycle being completed.
 */
export class CompletedWorkItemViewModel {
  /**
   * How many of the work item's units were completed.
   */
  @observable accessor completedItems = 0

  readonly fieldLengths = {
    completedItems: { min: 0 },
  } as const

  /**
   * Validation context for the completed count.
   */
  readonly validation = validationContext(this, {
    completedItems: [
      required(),
      () =>
        this.completedItemsAreValid ||
        `The number of completed items must be between ${this.fieldLengths.completedItems.min} and ${this.remaining}.`,
    ],
  })

  constructor(private readonly _workItem: WorkItem) {}

  /**
   * The work item's ID.
   */
  @computed get workItemId() {
    return this._workItem.workItemId
  }

  /**
   * How many of the work item's units remain.
   */
  @computed get remaining() {
    return this._workItem.remaining
  }

  /**
   * The name of the work item's inventory item.
   */
  @computed get inventoryItemName() {
    return this._workItem.inventoryItemName
  }

  /**
   * The description of the work item's inventory item.
   */
  @computed get inventoryItemDescription() {
    return this._workItem.inventoryItemDescription
  }

  /**
   * Whether the completed items quantity is valid
   */
  @computed
  get completedItemsAreValid() {
    return (
      this.completedItems <= this.remaining &&
      this.completedItems >= this.fieldLengths.completedItems.min
    )
  }

  /**
   * Resets the fields
   */
  @action.bound
  reset() {
    this.completedItems = 1
  }

  /**
   * Sets the number of completed items
   */
  @action.bound
  setCompletedItems(value: number | null) {
    if (!isNil(value)) {
      this.completedItems = value
    }
  }
}
